import React from "react";
import { Button, Card, ListGroup } from "react-bootstrap";
import { IConversion, IOrderDetails } from "../../util/utils";

interface OrderConfirmationStepProps {
    conversion: IConversion | null;
    orderDetails: IOrderDetails;
    onFinish: () => void;
}

const OrderConfirmationStep: React.FC<OrderConfirmationStepProps> = ({
    conversion,
    orderDetails,
    onFinish,
}) => {
    // Format currency for display
    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    return (
        <div className="order-confirmation-step">
            <div className="text-center mb-5">
                <div className="success-icon mb-4">
                    <i
                        className="bi bi-check-circle-fill text-success"
                        style={{ fontSize: "4rem" }}
                    ></i>
                </div>
                <h2 className="mb-3">Order Complete!</h2>
                <p className="lead mb-4">
                    Your order has been successfully processed. You will receive a confirmation
                    email shortly.
                </p>
            </div>

            <Card className="mb-4">
                <Card.Header>
                    <h4 className="mb-0">Order Summary</h4>
                </Card.Header>
                <Card.Body>
                    {orderDetails && orderDetails.submitData && (
                        <div className="mb-4">
                            <h5 className="border-bottom pb-2 mb-3">Customer Information</h5>
                            <p className="mb-1">
                                <strong>Name:</strong> {orderDetails.submitData.firstName}{" "}
                                {orderDetails.submitData.lastName}
                            </p>
                            <p className="mb-1">
                                <strong>Email:</strong> {orderDetails.submitData.email}
                            </p>
                            <p className="mb-1">
                                <strong>Phone:</strong> {orderDetails.submitData.phone}
                            </p>
                            <p className="mb-1">
                                <strong>Address:</strong> {orderDetails.submitData.address}
                                {orderDetails.submitData.address2 &&
                                    `, ${orderDetails.submitData.address2}`}
                            </p>
                            <p className="mb-1">
                                <strong>City:</strong> {orderDetails.submitData.city}
                            </p>
                            <p>
                                <strong>State/ZIP:</strong> {orderDetails.submitData.state},{" "}
                                {orderDetails.submitData.zip}
                            </p>
                        </div>
                    )}

                    {conversion && (
                        <div className="mb-4">
                            <h5 className="border-bottom pb-2 mb-3">Provider & Location</h5>
                            <p className="mb-1">
                                <strong>Provider:</strong> {conversion.practitionerName}
                            </p>
                            <p className="mb-1">
                                <strong>Location:</strong> {conversion.locationName}
                            </p>
                            <p className="mb-0">
                                <strong>Address:</strong> {conversion.locationAddress.fullAddress}
                            </p>
                        </div>
                    )}

                    <div>
                        <h5 className="border-bottom pb-2 mb-3">Purchased Services</h5>
                        {orderDetails &&
                        orderDetails.services &&
                        orderDetails.services.length > 0 ? (
                            <>
                                <ListGroup className="mb-3">
                                    {orderDetails.services.map((service) => (
                                        <ListGroup.Item
                                            key={service.id}
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <span>{service.careServiceName}</span>
                                                {service.requiresLab && (
                                                    <span className="badge bg-info ms-2">
                                                        Requires Lab
                                                    </span>
                                                )}
                                            </div>
                                            <span className="fw-bold">
                                                {formatCurrency(service.costInCents)}
                                            </span>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                                <div className="bg-light p-3 rounded">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">Total:</h5>
                                        <h5 className="mb-0">
                                            {formatCurrency(
                                                orderDetails.services.reduce(
                                                    (total: number, service) =>
                                                        total + service.costInCents,
                                                    0,
                                                ),
                                            )}
                                        </h5>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="mb-0">No services in this order</p>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card className="mb-4 border-success">
                <Card.Body className="text-center">
                    <h5 className="text-success mb-3">Next Steps</h5>
                    <p>
                        You'll receive detailed instructions in your email regarding any necessary
                        preparations for your services. If lab testing is required, you'll also
                        receive information on how to complete your tests.
                    </p>
                    <p className="mb-0">
                        If you have any questions, please contact us at support@lobbie.com or call
                        (555) 123-4567.
                    </p>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-center mt-4">
                <Button variant="primary" size="lg" onClick={onFinish}>
                    Return to Home
                </Button>
            </div>
        </div>
    );
};

export default OrderConfirmationStep;
